/**
 * Mirrors shared/patientPlatformFee.js for admin UI legacy-row reconstruction.
 */

const PATIENT_GST_PCT = 18;
const PATIENT_PF_PCT_BELOW = 5;
const PATIENT_PF_PCT_AT_OR_ABOVE = 4;
const PATIENT_FEE_TIER_THRESHOLD = 700;

const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;

export function patientPlatformFeePct(baseAmount) {
  const b = Number(baseAmount) || 0;
  return b >= PATIENT_FEE_TIER_THRESHOLD ? PATIENT_PF_PCT_AT_OR_ABOVE : PATIENT_PF_PCT_BELOW;
}

export function patientFeeBreakdownFromGross(gross, discount = 0) {
  const g = round2(gross);
  const target = round2(g + discount);
  const gstMult = 1 + PATIENT_GST_PCT / 100;
  const impliedB5 = round2(target / (1 + (PATIENT_PF_PCT_BELOW / 100) * gstMult));
  const impliedB4 = round2(target / (1 + (PATIENT_PF_PCT_AT_OR_ABOVE / 100) * gstMult));

  const tryBuild = (base) => {
    const b = round2(base);
    const pct = patientPlatformFeePct(b);
    const fee = round2(b * pct / 100);
    const gst = round2(target - b - fee);
    if (gst < -0.02) return null;
    if (Math.abs(round2(b + fee + gst) - target) > 0.02) return null;
    return { base: b, fee, gst, pct, discount, total: g };
  };

  const candidates = [];
  if (impliedB5 < PATIENT_FEE_TIER_THRESHOLD) {
    const hit = tryBuild(impliedB5);
    if (hit) candidates.push(hit);
  }
  if (impliedB4 >= PATIENT_FEE_TIER_THRESHOLD) {
    const hit = tryBuild(impliedB4);
    if (hit) candidates.push(hit);
  }
  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) {
    const isRoundFee = (n) => Math.abs(n - Math.round(n)) < 0.01;
    const roundOnes = candidates.filter((c) => isRoundFee(c.base));
    if (roundOnes.length === 1) return roundOnes[0];
    if (roundOnes.length > 1) {
      const hi = roundOnes.filter((c) => c.base >= PATIENT_FEE_TIER_THRESHOLD);
      if (hi.length) return hi.sort((a, b) => b.base - a.base)[0];
      return roundOnes.sort((a, b) => b.base - a.base)[0];
    }
    const exactB5 = candidates.find((c) => Math.abs(c.base - impliedB5) < 0.02);
    const exactB4 = candidates.find((c) => Math.abs(c.base - impliedB4) < 0.02);
    if (exactB5 && exactB5.base < PATIENT_FEE_TIER_THRESHOLD) return exactB5;
    if (exactB4 && exactB4.base >= PATIENT_FEE_TIER_THRESHOLD) return exactB4;
    candidates.sort((a, b) => {
      const score = (x) => Math.min(Math.abs(x.base - impliedB5), Math.abs(x.base - impliedB4));
      return score(a) - score(b) || a.base - b.base;
    });
    return candidates[0];
  }
  return tryBuild(impliedB5) || tryBuild(impliedB4) || { base: impliedB5, fee: 0, gst: 0, pct: 5, discount, total: g };
}
